import { InternalSmartFilterRuntime } from "./runtime/smartFilterRuntime.js";
import { OutputBlock } from "./blockFoundation/outputBlock.js";
import { RenderTargetGenerator } from "./runtime/renderTargetGenerator.js";
import { AggregateBlock } from "./blockFoundation/aggregateBlock.js";
import { ShaderBlock } from "./blockFoundation/shaderBlock.js";
import { GetBlockOutputTextureSize } from "./utils/textureUtils.js";
/**
 * How long to wait for shader compilation and texture loading to complete before erroring out.
 */
const InitializationTimeout = 10000;
/**
 * The smart filter class is the main class of the smart filter module.
 *
 * It is responsible for managing a graph of smart filter blocks.
 *
 * It is also responsible for creating the runtime associated to the current state of the filter.
 */
export class SmartFilter {
    /**
     * Creates a new instance of a @see SmartFilter.
     * @param name - The friendly name of the smart filter
     * @param namespace - The namespace of the smart filter
     */
    constructor(name, namespace = null) {
        /**
         * User defined comments to describe the current smart filter.
         */
        this.comments = null;
        /**
         * Data used by the smart filter editor.
         */
        this.editorData = null;
        this.name = name;
        this.namespace = namespace;
        this._attachedBlocks = new Array();
        this.outputBlock = new OutputBlock(this);
        this.output = this.outputBlock.input;
    }
    /**
     * @returns the list of blocks attached to the smart filter.
     */
    get attachedBlocks() {
        return this._attachedBlocks;
    }
    /**
     * @returns The current class name of the smart filter.
     */
    getClassName() {
        return "SmartFilter";
    }
    /**
     * Registers a block to be part of this smart filter.
     * @param block - The block to register on the smart filter
     * @throws if the block is already registered on another smart filter
     * @remarks This function will not register the block if it is already registered on the smart filter.
     */
    registerBlock(block) {
        // It is impossible to attach a block from another filter
        if (block.smartFilter !== this) {
            throw new Error("Block is not part of this smart filter");
        }
        // No need to attach a block several times
        if (this._attachedBlocks.indexOf(block) !== -1) {
            return;
        }
        // Add the block to the list of attached blocks
        this._attachedBlocks.push(block);
    }
    /**
     * Removes the block from the smart filter.
     * @param block - The block to remove from the smart filter
     * @remarks This function will disconnect the block on removal.
     * This Output block cannot be removed.
     */
    removeBlock(block) {
        const attachedBlockIndex = this._attachedBlocks.indexOf(block);
        // The block can only be removed if it is not the output block
        // and if it is attached to the smart filter
        if (attachedBlockIndex > -1 && !block.isOutput) {
            // Disconnects all the connections of the block
            block.disconnect();
            // Removes the block from the list of attached blocks
            this._attachedBlocks.splice(attachedBlockIndex, 1);
        }
    }
    _generateCommandsAndGatherInitPromises(initializationData) {
        const outputBlock = this.outputBlock;
        outputBlock.visit(initializationData, (block, initializationData) => {
            block.generateCommandsAndGatherInitPromises(initializationData, outputBlock.input.connectedTo?.ownerBlock === block);
        });
    }
    /**
     * Create a new runtime for the current state of the smart filter.
     * @param engine - The Babylon.js engine to use for the runtime
     * @param renderTargetGenerator - The render target generator to use to generate the RTTs for the shader blocks. If not provided, a default one will be created.
     * @returns the runtime that can be used to render the smart filter
     */
    async createRuntimeAsync(engine, renderTargetGenerator) {
        const runtime = new InternalSmartFilterRuntime(engine);
        const initializationData = {
            runtime,
            outputBlock: this.outputBlock,
            initializationPromises: [],
            disposableResources: [],
        };
        this._workWithAggregateFreeGraph(() => {
            this.outputBlock.prepareForRuntime();
            renderTargetGenerator = renderTargetGenerator ?? new RenderTargetGenerator(false);
            renderTargetGenerator.setOutputTextures(this, initializationData);
            this.outputBlock.propagateRuntimeData();
            this._generateCommandsAndGatherInitPromises(initializationData);
        });
        // Wait for all the blocks to be initialized
        if (initializationData.initializationPromises.length > 0) {
            const timeoutPromise = new Promise((resolve) => setTimeout(resolve, InitializationTimeout, true));
            // eslint-disable-next-line github/no-then
            const initializationPromises = Promise.all(initializationData.initializationPromises).then(() => false);
            const timedOut = await Promise.race([initializationPromises, timeoutPromise]);
            if (timedOut) {
                throw new Error("Initialization promises timed out");
            }
        }
        // Register the resources to dispose when the runtime is disposed
        initializationData.disposableResources.forEach((resource) => runtime.registerResource(resource));
        return runtime;
    }
    /**
     * Resizes any intermediate textures according to the new size of the render target
     * @param engine - The engine used to render the smart filter
     */
    resize(engine) {
        this._workWithAggregateFreeGraph(() => {
            this.outputBlock.visit({}, (block) => {
                if (!(block instanceof ShaderBlock)) {
                    return;
                }
                if (block.output.runtimeData?.value) {
                    const size = GetBlockOutputTextureSize(this, engine, block.outputTextureOptions);
                    block.output.runtimeData.value.resize(size);
                }
            });
        });
    }
    /**
     * @internal
     * Merges all aggregate blocks into the smart filter graph, executes the passed-in work, then restores the aggregate blocks.
     * @param work - The work to execute with the aggregate blocks merged
     */
    _workWithAggregateFreeGraph(work) {
        const mergedAggregateBlocks = [];
        // Merge all aggregate blocks
        this.outputBlock.visit({}, (block, _extraData) => {
            if (block instanceof AggregateBlock) {
                block._mergeIntoSmartFilter(mergedAggregateBlocks);
            }
        });
        try {
            // Do the passed in work
            work();
        }
        finally {
            // Restore all aggregate blocks, even if work throws
            for (const block of mergedAggregateBlocks) {
                block._unmergeFromSmartFilter();
            }
        }
    }
}
//# sourceMappingURL=smartFilter.js.map